import get from 'lodash/get';
import isNil from 'lodash/isNil';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { UNSUPPORTED_FIELD_TYPES_IN_GIC } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import type {
	FieldTabs,
	TimeTrackingSettings,
	FieldTab,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformStargateUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import type { TimeTrackingConfig } from '@atlassian/jira-issue-format-time/src/index.tsx';
import { PROJECT_PICKER_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { getGqlFieldMap, CONTEXT_FIELD_TYPES } from '../../constants/field-map.tsx';
import {
	PROJECT_FIELD,
	ISSUE_TYPE_FIELD,
	REQUEST_TYPE_FIELD,
	ISSUE_LINK_FIELD,
} from '../../constants/graphql-field-types.tsx';
import type {
	CreateConfigQueryResponse,
	FieldsDataWithPartialMeta,
	CreateSubtaskConfigQueryResponse,
	CreateConfigQueryResponseInContext,
	CreateAdditionalService,
	ProformaConfigurationService,
	WatchersConfigurationService,
	CreateLayoutItem,
	CreateSubtaskConfigQueryResponseWithML,
	CreateConfigQueryResponseInContextWithML,
	CreateConfigQueryResponseWithML,
	FetchConfigData,
} from '../../types/index.tsx';
import {
	transformProjectField,
	transformIssueTypeField,
	transformRequestTypeField,
} from '../context-fields/index.tsx';
import { getGqlUnsupportedFields, getQueryErrorMessage } from '../graphql-error-utils/index.tsx';
import { overrideGqlTypes } from '../type-overrides/index.tsx';

type GetTabLayoutInfoParams = {
	data: CreateConfigQueryResponse;
	onlyRequiredField?: boolean;
	allFieldIds: string[];
};

const getTabLayoutInfoOld = ({
	data,
	onlyRequiredField = false,
	allFieldIds = [],
}: GetTabLayoutInfoParams): FieldTabs => {
	const fieldTabs = get(data, 'jiraIssueCreateLayout.contentSection.items', [])
		.map(({ id, name, items, tabId }) => {
			if (onlyRequiredField) {
				// @ts-expect-error - TS7031 - Binding element 'fieldId' implicitly has an 'any' type.
				const filteredItems = items.filter(({ fieldId }) => allFieldIds.includes(fieldId));
				return filteredItems.length === 0
					? null
					: {
							id,
							name,
							items: filteredItems.map(
								// @ts-expect-error - TS7031 - Binding element 'fieldId' implicitly has an 'any' type.
								({ fieldId }) => fieldId,
							),
							...(fg('forge_ui_modifications_for_screen_tabs') && { tabId }),
						};
			}

			return {
				id,
				name,
				items: items
					// @ts-expect-error - TS7031 - Binding element 'fieldId' implicitly has an 'any' type.
					.filter(({ fieldId }) => allFieldIds.includes(fieldId))
					// @ts-expect-error - TS7031 - Binding element 'fieldId' implicitly has an 'any' type.
					.map(({ fieldId }) => fieldId),
				...(fg('forge_ui_modifications_for_screen_tabs') && { tabId }),
			};
		})
		.filter(Boolean);
	return fieldTabs;
};

const getTabLayoutInfoNew = ({
	data,
	onlyRequiredField = false,
	allFieldIds = [],
}: GetTabLayoutInfoParams): FieldTabs => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const fieldTabs = get(
		data,
		'jiraIssueCreateLayout.contentSection.items',
		[],
	) as CreateLayoutItem[];

	return fieldTabs
		.map(({ id, name, items, collapsedItems, tabId }) => {
			const filterItems = (itemsToFilter: CreateLayoutItem['items']) =>
				itemsToFilter
					.filter(({ fieldId }) => allFieldIds.includes(fieldId))
					.map(({ fieldId }) => fieldId);

			const filteredItems = filterItems(items || []);
			const filteredCollapsedItems = filterItems(collapsedItems || []);

			const result: FieldTab = {
				id,
				name,
				items: filteredItems,
				...(fg('forge_ui_modifications_for_screen_tabs') && { tabId }),
			};

			result.collapsedItems = filteredCollapsedItems;
			if (onlyRequiredField && result.items.length === 0 && result.collapsedItems.length === 0)
				return null;

			return result;
		})
		.filter((tab): tab is FieldTab => !!tab);
};

export const getTabLayoutInfo = ({
	data,
	onlyRequiredField = false,
	allFieldIds = [],
}: GetTabLayoutInfoParams): FieldTabs => {
	if (ff('gic-smart-fields-jira-labs_8uwvr') || fg('migrate_gic_smart_fields_jira_labs'))
		return getTabLayoutInfoNew({ data, onlyRequiredField, allFieldIds });
	return getTabLayoutInfoOld({ data, onlyRequiredField, allFieldIds });
};

export const transformTimeTrackingSettings = (
	timeTrackingSettings?: TimeTrackingSettings,
): TimeTrackingConfig | undefined => {
	if (timeTrackingSettings) {
		const {
			defaultFormat,
			workingDaysPerWeek,
			workingHoursPerDay,
			defaultUnit,
			isJiraConfiguredTimeTrackingEnabled,
		} = timeTrackingSettings;

		let format = '';

		if (defaultFormat === 'DAYS') {
			format = 'days';
		} else if (defaultFormat === 'HOURS') {
			format = 'hours';
		} else {
			format = 'pretty';
		}

		let unit = '';
		if (defaultUnit === 'DAY') {
			unit = 'day';
		} else if (defaultUnit === 'HOUR') {
			unit = 'hour';
		} else if (defaultUnit === 'MINUTE') {
			unit = 'minute';
		} else {
			unit = 'week';
		}

		return {
			daysPerWeek: workingDaysPerWeek,
			hoursPerDay: workingHoursPerDay,
			// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Format | undefined'.
			format,
			// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'DefaultUnit'.
			defaultUnit: unit,
			isTimeTrackingEnabled: isJiraConfiguredTimeTrackingEnabled,
		};
	}
	return undefined;
};

// TODO: typing needs to be improved here
export const getFieldsKeyedById = (
	data: CreateConfigQueryResponse,
	onlyRequiredField = false,
): FieldsDataWithPartialMeta => {
	const fields = get(data, 'jiraIssueCreateFields.fields.nodes', []);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const contextFields: Record<string, any> = {};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const fieldConfigData: Record<string, any> = {};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const requiredFieldConfigData: Record<string, any> = {};
	let isRequiredFieldComponentSupported = true;
	let gqlTypeBeingProcessed: string;
	fields.forEach((fieldData) => {
		try {
			const {
				__typename: gqlType,
				fieldId,
				name,
				type,
				description,
				fieldConfig = {},
				searchUrl,
				mediaContext,
				adminRichTextConfig,
				isMulti,
				attributesIncludedInAutoCompleteSearch,
				timeTrackingSettings,
				maxAllowedTotalAttachmentsSize,
			} = fieldData;

			gqlTypeBeingProcessed = gqlType;
			if (fieldId && !UNSUPPORTED_FIELD_TYPES_IN_GIC.includes(type)) {
				if (CONTEXT_FIELD_TYPES.includes(gqlType) && type !== PROJECT_PICKER_CF_TYPE) {
					let transformedData;
					if (gqlType === PROJECT_FIELD) {
						transformedData = transformProjectField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === ISSUE_TYPE_FIELD) {
						transformedData = transformIssueTypeField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === REQUEST_TYPE_FIELD) {
						const requestTypeFieldData = fieldData;
						// @ts-expect-error - TS2339 Property 'requestType' does not exist on type 'never'.
						if (requestTypeFieldData?.requestType?.issueType) {
							// @ts-expect-error - TS2339 Property 'requestType' does not exist on type 'never'.
							requestTypeFieldData.requestType.issueType.issueTypeName =
								contextFields?.issuetype?.selectedIssueType?.label;
						}
						transformedData = transformRequestTypeField(requestTypeFieldData);
						contextFields.requestType = transformedData;
					}
				} else {
					const gqlTypeWithOverride = overrideGqlTypes(fieldData);
					const gqlFieldConfig =
						// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly JiraAffectedServicesField: { readonly Component: AbstractComponent<ReturnComponentProps<AffectedServicesFieldConfig>, any>; readonly defaultValueKey: "selectedAffectedServices"; readonly submitTransform: (options: AffectedServiceOption[]) => ServiceEntity[]; readonly retainUserInputTransform: (selectedAff...'.
						getGqlFieldMap()[gqlTypeWithOverride] || {};
					const { defaultValueKey, allowedValuesKey } = gqlFieldConfig;

					let defaultValue;
					if (Array.isArray(defaultValueKey)) {
						defaultValue = defaultValueKey.map(
							(key: string) => key && get(fieldData, key, undefined),
						);
					} else {
						defaultValue = defaultValueKey && get(fieldData, defaultValueKey, undefined);
					}

					const allowedValues = allowedValuesKey && get(fieldData, allowedValuesKey, undefined);
					// @ts-expect-error - TS2339 Property 'isRequired' does not exist on type '{}'.
					const isRequired = fieldConfig?.isRequired || false;
					// @ts-expect-error - TS2339 Property 'isEditable' does not exist on type '{}'.
					const isEditable = fieldConfig?.isEditable;
					// @ts-expect-error - TS2339 Property 'nonEditableReason' does not exist on type '{}'.
					const nonEditableReason = fieldConfig?.nonEditableReason || {};
					const copyLinksAndAttachments =
						gqlTypeWithOverride === ISSUE_LINK_FIELD &&
						fg('create_linked_issues_trigger_point_issue_view')
							? // @ts-expect-error - TS2339 Property 'copyLinksAndAttachments' does not exist on type 'never'.
								fieldData?.copyLinksAndAttachments
							: undefined;

					fieldConfigData[fieldId] = {
						gqlType: gqlTypeWithOverride,
						fieldId,
						fieldName: name,
						fieldType: type,
						description,
						isRequired,
						isEditable,
						nonEditableReason,
						autoCompleteUrl: transformStargateUrl(searchUrl || ''),
						defaultValue: isNil(defaultValue) ? undefined : defaultValue,
						allowedValues,
						additionalData: {
							mediaContext,
							isMulti,
							attributesIncludedInAutoCompleteSearch,
							timeTrackingSettings: transformTimeTrackingSettings(timeTrackingSettings),
							maxAllowedTotalAttachmentsSize,
							...(fg('create_linked_issues_trigger_point_issue_view')
								? { copyLinksAndAttachments }
								: {}),
						},
					};
					if (adminRichTextConfig) {
						fieldConfigData[fieldId].additionalData.adminRichTextConfig = adminRichTextConfig;
					}

					if (onlyRequiredField && isRequired) {
						requiredFieldConfigData[fieldId] = fieldConfigData[fieldId];
					}
					if (isRequired && !('Component' in gqlFieldConfig)) {
						isRequiredFieldComponentSupported = false;
					}
				}
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'processingApiFieldData',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				attributes: {
					error: `processing ${gqlTypeBeingProcessed} failed`,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	});

	// process fields which are not supported in graphql layer
	const errors = get(data, 'jiraIssueCreateFields.errors', []);
	const { unsupportedFields, isRequiredFieldTypeSupported } = getGqlUnsupportedFields(errors);

	return {
		fieldConfig: onlyRequiredField
			? { ...requiredFieldConfigData }
			: { ...fieldConfigData, ...unsupportedFields },
		contextFields,
		meta: {
			areRequiredFieldsSupported: isRequiredFieldTypeSupported && isRequiredFieldComponentSupported,
		},
	};
};

export const getServicesKeyedByServiceId = (data: CreateConfigQueryResponse) => {
	const nodes: { node: CreateAdditionalService }[] = get(
		data,
		'jiraIssueCreateAdditionalServices.services.edges',
		[],
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const services = {} as Record<CreateAdditionalService['serviceId'], CreateAdditionalService>;

	nodes.forEach(({ node }) => {
		services[node.serviceId] = node;
	});

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return services as {
		watchers?: WatchersConfigurationService;
		proforma?: ProformaConfigurationService;
	};
};

export const transformQueryResults = (
	data: CreateConfigQueryResponse,
	onlyRequiredField = false,
) => {
	const fieldKeyedById = getFieldsKeyedById(data, onlyRequiredField);

	const { watchers, proforma } = getServicesKeyedByServiceId(data);

	const result = {
		fieldTabs: getTabLayoutInfo({
			data,
			onlyRequiredField,
			allFieldIds: Object.keys(fieldKeyedById.fieldConfig),
		}),
		...fieldKeyedById,
		meta: {
			...fieldKeyedById.meta,
			queryErrorMessage: getQueryErrorMessage(data),
		},
		additionalServices: {
			watchersConfigurations: watchers?.permissions,
			proformaConfiguration: proforma?.proforma,
		},
	};

	return validateRequiredFieldsFromTransformQueryResults(result);
};

const validateRequiredFieldsFromTransformQueryResults = (result: FetchConfigData) => {
	const { fieldTabs, fieldConfig } = result;

	const newFieldTabs = fieldTabs.map((tab) => {
		if (!tab.collapsedItems) return tab;

		const collapsedItems = tab.collapsedItems.filter((fieldId) => {
			const field = fieldConfig[fieldId];
			if (field?.isRequired || fieldId === 'summary') {
				return false;
			}
			return true;
		});

		return {
			...tab,
			collapsedItems,
		};
	});

	return {
		...result,
		fieldTabs: newFieldTabs,
	};
};

export const transformQueryData = (
	data: CreateConfigQueryResponse | CreateConfigQueryResponseWithML,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraIssueCreateFields },
	...(data.jiraIssueCreateAdditionalServices
		? { jiraIssueCreateAdditionalServices: { ...data.jiraIssueCreateAdditionalServices } }
		: {}),
	jiraIssueCreateLayout: Object.prototype.hasOwnProperty.call(data, 'jiraIssueCreateLayoutWithML')
		? {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				...(data as CreateConfigQueryResponseWithML).jiraIssueCreateLayoutWithML,
			}
		: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			{ ...(data as CreateConfigQueryResponse).jiraIssueCreateLayout },
});

const tranformSubtaskDataOld = (
	data: CreateSubtaskConfigQueryResponse,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraSubtaskCreationFields },
	jiraIssueCreateLayout: { ...data.jiraSubtaskCreationLayout },
	jiraIssueCreateAdditionalServices: { ...data.jiraSubtaskCreationAdditionalServices },
});

const tranformSubtaskDataNew = (
	data: CreateSubtaskConfigQueryResponse | CreateSubtaskConfigQueryResponseWithML,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraSubtaskCreationFields },
	jiraIssueCreateAdditionalServices: {
		...data.jiraSubtaskCreationAdditionalServices,
	},
	jiraIssueCreateLayout: Object.prototype.hasOwnProperty.call(
		data,
		'jiraSubtaskCreationLayoutWithML',
	)
		? {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				...(data as CreateSubtaskConfigQueryResponseWithML).jiraSubtaskCreationLayoutWithML,
			}
		: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			{ ...(data as CreateSubtaskConfigQueryResponse).jiraSubtaskCreationLayout },
});

export const tranformSubtaskData = (
	data: CreateSubtaskConfigQueryResponse | CreateSubtaskConfigQueryResponseWithML,
) => {
	if (ff('gic-smart-fields-jira-labs_8uwvr') || fg('migrate_gic_smart_fields_jira_labs'))
		return tranformSubtaskDataNew(data);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return tranformSubtaskDataOld(data as CreateSubtaskConfigQueryResponse);
};

export const tranformModalLoadDataInContextOld = (
	data: CreateConfigQueryResponseInContext,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraIssueCreateFieldsInContext },
	jiraIssueCreateLayout: { ...data.jiraIssueCreateLayoutInContext },
	jiraIssueCreateAdditionalServices: { ...data.jiraIssueCreateAdditionalServices },
});

export const tranformModalLoadDataInContextNew = (
	data: CreateConfigQueryResponseInContext | CreateConfigQueryResponseInContextWithML,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraIssueCreateFieldsInContext },
	jiraIssueCreateAdditionalServices: { ...data.jiraIssueCreateAdditionalServices },
	jiraIssueCreateLayout: Object.prototype.hasOwnProperty.call(
		data,
		'jiraIssueCreateLayoutInContextWithML',
	)
		? {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				...(data as CreateConfigQueryResponseInContextWithML).jiraIssueCreateLayoutInContextWithML,
			}
		: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			{ ...(data as CreateConfigQueryResponseInContext).jiraIssueCreateLayoutInContext },
});

export const tranformModalLoadDataInContext = (
	data: CreateConfigQueryResponseInContext | CreateConfigQueryResponseInContextWithML,
): CreateConfigQueryResponse => {
	if (ff('gic-smart-fields-jira-labs_8uwvr') || fg('migrate_gic_smart_fields_jira_labs'))
		return tranformModalLoadDataInContextNew(data);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return tranformModalLoadDataInContextOld(data as CreateConfigQueryResponseInContext);
};
